<template>
  <v-form
    ref="form"
    v-model="valid"
    lazy-validation
    class="py-4"
    @submit.prevent="submit"
    @keypress.enter="submit"
  >
    <v-row justify="center">
      <v-col cols="12" md="6">
        <t-document-input
          v-model="user.form.model.document"
          :document-type="user.form.model.document_type"
          hide-details="auto"
          required
          disabled
        />
      </v-col>

      <v-col cols="12" md="6">
        <t-gender-input
          v-model="user.form.model.gender_id"
          hide-details="auto"
          required
          label="Gênero"
        />
      </v-col>

      <v-col cols="12" md="12">
        <t-text-input
          v-model="user.form.model.name"
          label="Nome Completo"
          required
          hide-details="auto"
          :rules="nameRules"
        />
      </v-col>

      <v-col cols="12" md="6">
        <t-phone-input
          v-model="user.form.model.cellphone"
          required
          hide-details="auto"
          label="Telefone"
        />
      </v-col>

      <v-col cols="12" md="6">
        <t-date-input
          v-model="user.form.model.birth_date"
          label="Data de Nascimento"
          hide-details="auto"
          required
        />
      </v-col>

      <v-col cols="12" md="6">
        <t-email-input
          v-model="user.form.model.email"
          required
          hide-details="auto"
          :error-messages="emailErrorBag"
          @input="() => emailErrorBag = []"
          @blur="checkEmail"
        />
      </v-col>

      <v-col cols="12" md="6">
        <t-text-input
          v-model="confirmEmail"
          label="Confirme seu e-mail "
          :rules="confirmEmailRules"
          hide-details="auto"
          @paste.prevent
        />
      </v-col>

      <v-col cols="12" md="6">
        <t-password-input
          v-model="user.form.model.password"
          label="Senha"
          required
          hide-details="auto"
          :show="showPassword"
          @click:append="showPassword = !showPassword"
        />
      </v-col>

      <v-col cols="12" md="6">
        <t-password-input
          v-model="confirmPassword"
          label="Confirme sua senha "
          :show="showPassword"
          :rules="confirmPasswordRules"
          hide-details="auto"
          @click:append="showPassword = !showPassword"
        />
      </v-col>

      <v-col cols="12" class="d-flex justify-center">
        <v-col cols="12" class="pa-0">
          <t-btn
            color="grey"
            block
            class="heading--text"
            large
            @click="$emit('cancel')"
          >
            Voltar
          </t-btn>
        </v-col>
      </v-col>

      <v-col cols="12" class="d-flex justify-center">
        <v-col cols="12" class="pa-0">
          <t-btn
            color="success"
            block
            type="submit"
            class="heading--text"
            large
            :disabled="!valid"
          >
            Avançar
          </t-btn>
        </v-col>
      </v-col>
    </v-row>
  </v-form>
</template>

<script setup lang="ts">
import { ref, computed, useTemplateRef, type Ref } from "vue";
import TBtn from "@/components/core/TBtn.vue";
import TDateInput from "@/components/core/TDateInput.vue";
import TEmailInput from "@/components/core/TEmailInput.vue";
import TPasswordInput from "@/components/core/TPasswordInput.vue";
import TPhoneInput from "@/components/core/TPhoneInput.vue";
import TTextInput from "@/components/core/TTextInput.vue";
import TDocumentInput from "@/components/core/TDocumentInput.vue";
import userExists from "@/api/requests/userExists";
import { useUserStore } from "@/stores/user";
import { personName } from "@/utils/name";
import TGenderInput from "../core/TGenderInput.vue";

const user = useUserStore();

const emit = defineEmits<{
  cancel: [];
  submit: [];
}>();

defineOptions({
  name: "FormUserBasicData",
});

const form = useTemplateRef<{ validate: () => Promise<{ valid: boolean }> }>("form");

const emailErrorBag: Ref<string[]> = ref([]);
const confirmEmail: Ref<string> = ref("");
const confirmPassword: Ref<string> = ref("");
const showPassword: Ref<boolean> = ref(false);
const valid: Ref<boolean> = ref(true);

const nameRules = computed(() => [
  (): boolean | string =>
    !user.form.model.name || personName.validate(user.form.model.name),
  (): boolean | string =>
    !user.form.model.name || user.form.model.name.length > 2 || "O nome precisa ter mais que 2 letras.",
]);

const confirmEmailRules = computed(() => [
  (v?: string): boolean | string =>
    !user.form.model.email || v === user.form.model.email || "Os e-mails informados são diferentes",
]);

const confirmPasswordRules = computed(() => [
  (v?: string): boolean | string =>
    !user.form.model.password || v === user.form.model.password || "As senhas informadas são diferentes",
]);

async function checkEmail(): Promise<void> {
  const result = await userExists("email", user.form.model.email);

  if (result) {
    emailErrorBag.value = [ "Esse e-mail já está em uso" ];
  }
}

async function submit(): Promise<void> {
  const result = await form.value?.validate();

  if (!result?.valid) return;

  emit("submit");
}

</script>
